import { Button, DisplayItemsPerPageSelect, Paginator, SecondaryButton } from 'components';
import { AdminList } from '../AdminList';
import { StyledAdminEditDeviceGroups } from './styles';
import { DataExportButton } from '../DataExportButton';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { RootState, useAppDispatch, useAppSelector } from 'store/store';
import {
  selectAdminDeviceGroups,
  setItemsPerDeviceGroupsListPage,
  deviceGroupsListSetPage,
} from 'store/slices/adminDeviceGroupsSlice';
import { IAdminDeviceGroup } from 'types';
import { useSelector } from 'react-redux';
import { fetchExportDeviceGroups, fetchDeviceGroups } from 'store/thunks/adminDeviceGroupsThunk';
import { RefreshButton } from '../RefreshButton';
import { AdminEditInput } from '../AdminEditInput';
import { AdminDeviceGroupsListHeader } from '../AdminDeviceGroupsListHeader';
import { AdminDeviceGroupsListRow } from '../AdminDeviceGroupsListRow';
import { downloadCsvFile } from 'utils';
import { EApiResponseStatus } from 'types/api';
import { AdminRepository } from 'repositories/AdminRepository';
import { VALIDATION_ERROR_ADMIN_GROUP_ADJUST_REQUIRED } from 'constants/errorsConstants';
import { AdminDeviceGroupsSumRow } from '../AdminDeviceGroupsSumRow';
import { AdminConfirmModal } from '../AdminConfirmModal';

interface IAdminEditPaymentsProps {
  children?: React.ReactNode;
}

export const AdminEditDeviceGroups = ({ children }: IAdminEditPaymentsProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { groups, sums } = useSelector(selectAdminDeviceGroups);
  const { token } = useAppSelector((state) => state.auth);
  const { newbieAdjust } = useAppSelector((state) => state.adminCosts);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedAll, setSelectedAll] = useState(false);
  const [selectedDeviceGroupsIds, setSelectedDeviceGroupsIds] = useState<IAdminDeviceGroup['id'][]>(
    [],
  );
  const { pagesQuantity, currentPage, itemsPerPage } = useSelector(
    (state: RootState) => state.adminDeviceGroups,
  );

  const changeCurrentPageHandler = (currentPage: number) => {
    dispatch(deviceGroupsListSetPage(currentPage));
    dispatch(fetchDeviceGroups());
  };

  const changeItemsPerPageQuantityHandler = (itemsQuantity: number) => {
    dispatch(setItemsPerDeviceGroupsListPage(itemsQuantity));
    dispatch(fetchDeviceGroups());
  };

  const refreshButtonHandler = () => {
    dispatch(fetchDeviceGroups());
  };

  const dataExportButtonHandler = async () => {
    const data = await dispatch(fetchExportDeviceGroups()).unwrap();
    downloadCsvFile(data.exportData, 'Yesterday`s hashrate and payments');
  };

  const submitAddGroupHandler = async () => {
    // Добавление группы
  };

  const closeConfirmModal = () => {
    setShowConfirmModal(false);
  };

  const submitDeleteActionHandler = () => {
    setShowConfirmModal(true);
  };

  const submitDeleteGroupAction = async () => {
    submitDeleteGroup();
    setShowConfirmModal(false);
  };

  const submitDeleteGroup = async () => {
    // Удаление группы
  };

  const submitEditDeviceAdjust = async (amount: number) => {
    if (selectedDeviceGroupsIds.length === 0) return;

    try {
      if (!token) throw Error('no token or unathorised');
      if (typeof amount !== 'number') throw Error(VALIDATION_ERROR_ADMIN_GROUP_ADJUST_REQUIRED);

      const requests = selectedDeviceGroupsIds
        .map((id) => {
          const group = groups.find((group) => group.id === id);
          if (!group) throw new Error(`Group with ID ${id} not found`);
          return group;
        })
        .filter((group) => Number(group.adjust) !== amount)
        .map((group) =>
          AdminRepository.setDeviceGroupAdjust(token, {
            groupId: group.id,
            adjust: amount,
          }),
        );

      if (requests.length === 0) return;

      const responses = await Promise.all(requests);

      const failedResponse = responses.find((res) => res.status === EApiResponseStatus.FAILURE);
      if (failedResponse) throw new Error(failedResponse.error);

      await dispatch(fetchDeviceGroups());
      setSelectedDeviceGroupsIds([]);
      setSelectedAll(false);
    } catch (error) {
      console.error('submit user adjust error', error);
    }
  };

  const selectAllHandler = (isSelectedAll: boolean) => {
    if (isSelectedAll) {
      const allIds = groups.map((el) => el.id);
      setSelectedDeviceGroupsIds(allIds);
      setSelectedAll(true);
    } else {
      setSelectedDeviceGroupsIds([]);
      setSelectedAll(false);
    }
  };

  const selectHanler = (id: IAdminDeviceGroup['id'], selected: boolean) => {
    if (selected) {
      setSelectedDeviceGroupsIds((prev) => [...prev, id]);
    } else {
      const updatedSelectedElementsIds = selectedDeviceGroupsIds.filter(
        (selectedId) => selectedId !== id,
      );
      setSelectedDeviceGroupsIds(updatedSelectedElementsIds);
    }
  };

  useEffect(() => {
    if (selectedAll && selectedDeviceGroupsIds.length !== groups.length) setSelectedAll(false);
  }, [selectedAll, selectedDeviceGroupsIds]);

  return (
    <StyledAdminEditDeviceGroups>
      <AdminList
        renderControls={() => (
          <div className="admin-edit-device-controls">
            <AdminEditInput
              onSubmit={submitEditDeviceAdjust}
              heading={t('adminPage.changeAdjust')}
              modalHeading={t('adminPage.confirmModal.adjustChange')}
              cost={newbieAdjust || 0}
            />
            <Button
              title={t('adminPage.editDeviceGroups.addGroup')}
              onClick={submitAddGroupHandler}
            />
            <SecondaryButton
              title={t('adminPage.editDeviceGroups.deleteGroup')}
              onClick={submitDeleteActionHandler}
            />
            {showConfirmModal && (
              <AdminConfirmModal
                title={t('adminPage.confirmModal.deleteGroup')}
                onConfirm={submitDeleteGroupAction}
                onCancel={closeConfirmModal}
                show={showConfirmModal}
              />
            )}
            <div className="admin-edit-device-controls__right">
              <DataExportButton onClick={dataExportButtonHandler} />
              <RefreshButton onClick={refreshButtonHandler} />
            </div>
          </div>
        )}
      >
        <div className="list-body">
          <AdminDeviceGroupsListHeader onSelectAll={selectAllHandler} selectedAll={selectedAll} />
          {groups.map((group) => (
            <AdminDeviceGroupsListRow
              key={group.id}
              selected={selectedDeviceGroupsIds.includes(group.id)}
              onSelect={selectHanler}
              group={group}
            />
          ))}
          <AdminDeviceGroupsSumRow sums={sums} />
        </div>
      </AdminList>
      {pagesQuantity > 0 && (
        <div className="list-body-pagination">
          <Paginator
            onChange={changeCurrentPageHandler}
            pageQuantity={pagesQuantity}
            currentPage={currentPage}
          />
          <DisplayItemsPerPageSelect
            onChange={changeItemsPerPageQuantityHandler}
            currentItemsPerPageQuantity={itemsPerPage}
            itemsPerPageRates={[10, 15, 25, 50]}
          />
        </div>
      )}
    </StyledAdminEditDeviceGroups>
  );
};
